/** NFA Register — faceted search, bulk upload, signed-PDF intake, create wizard. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Download, FileUp, Loader2, Plus, Search, Save, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useNfaTheme, money, day } from './nfaTheme';
import { Btn, Drawer, Empty, Field, Input, Panel, Pill, Select } from './NfaPrimitives';
import { Combo, LockedValue, MoneyStepper, WbsPicker } from './NfaPickers';
import { CognaraDraftAssist } from './NfaCognara';
import NfaDetailDrawer from './NfaDetailDrawer';

const STEPS = ['Basics', 'Financials', 'Justification', 'Attachments', 'Route preview'];

const GUARD_TONE = { OK: 'green', WARN: 'amber', BREACH: 'red', NO_BUDGET: 'accent', UNKNOWN: 'muted' };

/** Spend guardrail — does this note push its WBS element past budget? */
const GuardrailBanner = ({ T, projectId, wbs, amount }) => {
  const [g, setG] = useState(null);
  useEffect(() => {
    if (!projectId || !wbs) { setG(null); return; }
    api.get(`/nfa/assist/guardrail?project_id=${projectId}&wbs_element=${encodeURIComponent(wbs)}&amount=${amount || 0}`)
      .then((r) => setG(r.data)).catch(() => setG(null));
  }, [projectId, wbs, amount]);
  if (!g) return null;
  const c = T[GUARD_TONE[g.status] || 'muted'] || T.muted;
  return (
    <div className="rounded-lg px-3 py-2.5" data-testid="nfa-guardrail"
      style={{ background: T.tint(c), border: `1px solid ${T.tint(c, '44')}` }}>
      <div className="flex items-center gap-2 flex-wrap">
        <ShieldAlert className="w-3.5 h-3.5" style={{ color: c }} />
        <span className="text-[10px] uppercase tracking-[0.1em] font-bold" style={{ color: c }}
          data-testid="nfa-guardrail-status">spend guardrail · {g.status.replace('_', ' ')}</span>
        {g.utilisationPct != null && (
          <span className="text-[10px] tabular-nums" style={{ color: T.muted }}>
            {g.utilisationPct}% of {g.wbs_element} budget after this note
          </span>
        )}
      </div>
      <p className="text-[11.5px] mt-1" style={{ color: T.ink }}>{g.message}</p>
      {g.budget > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-2">
          {[['Budget', g.budget], ['Incurred', g.incurred], ['Committed', g.committed],
            [`In-flight NFAs (${g.inFlightNfaCount})`, g.inFlightNfaValue],
            ['Headroom', g.headroom]].map(([l, v]) => (
            <div key={l}>
              <div className="text-[9px] uppercase tracking-[0.08em]" style={{ color: T.muted }}>{l}</div>
              <div className="text-[11px] font-semibold tabular-nums" style={{ color: T.ink }}>{money(v)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CreateWizard = ({ meta, onClose, onCreated, T }) => {
  const activeProject = meta?.activeProject || meta?.projects?.[0] || null;
  const [step, setStep] = useState(0);
  const [files, setFiles] = useState([]);
  const [draft, setDraft] = useState({
    title: '', nfa_type: 'Contract Award', project_id: activeProject?.project_id || '',
    wbs_element: '', requested_amount: '', currency: 'INR', vendor_name: '',
    justification: '', business_case: '', required_by_date: '' });
  const [id, setId] = useState(null);
  const [route, setRoute] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  // Autosave as DRAFT on every step change.
  const persist = async () => {
    setBusy(true);
    try {
      if (!id) {
        const r = await api.post('/nfa', { ...draft, requested_amount: Number(draft.requested_amount || 0) });
        setId(r.data.nfa_id);
        toast.success(`Draft ${r.data.nfa_no} saved`);
        return r.data.nfa_id;
      }
      await api.patch(`/nfa/${id}`, { ...draft, requested_amount: Number(draft.requested_amount || 0) });
      return id;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the draft');
      return null;
    } finally { setBusy(false); }
  };

  const next = async () => {
    if (step === 0 && (!draft.title || !draft.project_id || !draft.wbs_element)) {
      toast.error('Title, project and WBS element are required'); return;
    }
    const saved = await persist();
    if (!saved) return;
    if (step === 3) {
      const r = await api.post('/nfa/route-preview', {
        project_id: draft.project_id, nfa_type: draft.nfa_type,
        requested_amount: Number(draft.requested_amount || 0) });
      setRoute(r.data);
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };

  const upload = async (file) => {
    if (!file || !id) return;
    const fd = new FormData();
    fd.append('file', file); fd.append('doc_category', 'Quotation');
    await api.post(`/nfa/${id}/attachments`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } });
    setFiles((f) => [...f, file.name]);
    toast.success(`${file.name} attached`);
  };

  return (
    <Drawer open onClose={onClose} T={T} testId="nfa-create-wizard" width={680}
      title="Raise a Note for Approval"
      subtitle={<div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
        {STEPS.map((s, i) => (
          <span key={s} className="text-[9.5px] px-2 py-0.5 rounded-full"
            style={{ background: i === step ? T.primary : T.tint(T.primary),
                     color: i === step ? '#fff' : T.primary }}>{i + 1}. {s}</span>
        ))}
      </div>}>
      {step === 0 && (
        <div className="space-y-3" data-testid="nfa-wizard-basics">
          <CognaraDraftAssist T={T} draft={draft} attachmentNames={files}
            onApply={(patch) => setDraft((d) => ({ ...d, ...patch }))} />
          <Field label="Title" T={T}>
            <Input T={T} value={draft.title} onChange={set('title')} data-testid="nfa-new-title"
              placeholder="Award of civil works package C-104 to Petrofac" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="NFA type" T={T}>
              <Combo T={T} value={draft.nfa_type} testId="nfa-new-type"
                onChange={(v) => setDraft((d) => ({ ...d, nfa_type: v }))}
                options={(meta?.types || []).map((t) => ({ value: t, label: t }))} />
            </Field>
            <Field label="Project" T={T}>
              <LockedValue T={T} testId="nfa-new-project"
                hint="Scoped to the project selected in the header — switch projects there to raise elsewhere">
                {activeProject?.name || activeProject?.project_id || '— no project selected —'}
              </LockedValue>
            </Field>
            <Field label="WBS element" T={T}
              hint="The work-breakdown branch this approval is charged to — pick it, never type it">
              <WbsPicker T={T} projectId={draft.project_id} value={draft.wbs_element}
                onChange={(v) => setDraft((d) => ({ ...d, wbs_element: v }))}
                testId="nfa-new-wbs" />
            </Field>
            <Field label="Required by" T={T}>
              <Input T={T} type="date" value={draft.required_by_date}
                onChange={set('required_by_date')} data-testid="nfa-new-required-by" />
            </Field>
          </div>
        </div>
      )}
      {step === 1 && (
        <div className="grid grid-cols-2 gap-3" data-testid="nfa-wizard-financials">
          <Field label="Requested amount" T={T}>
            <MoneyStepper T={T} value={Number(draft.requested_amount || 0)} testId="nfa-new-amount"
              onChange={(v) => setDraft((d) => ({ ...d, requested_amount: v }))} />
          </Field>
          <Field label="Currency" T={T}>
            <Combo T={T} value={draft.currency} testId="nfa-new-currency"
              onChange={(v) => setDraft((d) => ({ ...d, currency: v }))}
              options={(meta?.currencies || ['INR']).map((c) => ({ value: c, label: c }))} />
          </Field>
          <Field label="Vendor (optional)" T={T}>
            <Input T={T} value={draft.vendor_name} onChange={set('vendor_name')} data-testid="nfa-new-vendor" />
          </Field>
        </div>
      )}
      {step === 2 && (
        <div className="space-y-3" data-testid="nfa-wizard-justification">
          <Field label="Justification" T={T}>
            <textarea value={draft.justification} onChange={set('justification')} rows={6}
              data-testid="nfa-new-justification"
              className="w-full text-[12px] rounded-md px-2.5 py-2 outline-none"
              style={{ background: T.surface, border: `1px solid ${T.border}`, color: T.ink }} />
          </Field>
          <Field label="Business case" T={T}>
            <textarea value={draft.business_case} onChange={set('business_case')} rows={4}
              data-testid="nfa-new-business-case"
              className="w-full text-[12px] rounded-md px-2.5 py-2 outline-none"
              style={{ background: T.surface, border: `1px solid ${T.border}`, color: T.ink }} />
          </Field>
        </div>
      )}
      {step === 3 && (
        <div className="space-y-2" data-testid="nfa-wizard-attachments">
          <p className="text-[11.5px]" style={{ color: T.body }}>
            Attach quotations, comparative statements and evaluations. Up to 50 MB each; the same
            filename re-uploaded becomes a new version.
          </p>
          <input ref={fileRef} type="file" multiple className="hidden" data-testid="nfa-wizard-file"
            onChange={(e) => Array.from(e.target.files || []).forEach(upload)} />
          <Btn T={T} variant="ghost" onClick={() => fileRef.current?.click()}
            testId="nfa-wizard-upload">Choose files</Btn>
        </div>
      )}
      {step === 4 && (
        <div className="space-y-3" data-testid="nfa-wizard-route">
          <GuardrailBanner T={T} projectId={draft.project_id} wbs={draft.wbs_element}
            amount={Number(draft.requested_amount || 0)} />
          <p className="text-[11.5px]" style={{ color: T.body }}>
            Resolved live from the approval matrix — {route?.totalLevels ?? 0} active of{' '}
            {route?.configuredLevels ?? 0} configured levels for this project, type and amount.
            You are excluded from the chain under maker-checker.
          </p>
          <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${T.border}` }}>
            {(route?.route || []).map((lv) => (
              <div key={lv.level_no} className="flex items-center gap-2.5 px-3 py-2 text-[11px]"
                style={{ borderTop: `1px solid ${T.border}` }}
                data-testid={`nfa-route-level-${lv.level_no}`}>
                <span className="w-5 h-5 rounded grid place-items-center text-[10px] font-bold tabular-nums"
                  style={{ background: T.tint(T.primary), color: T.primary }}>{lv.level_no}</span>
                <span style={{ color: T.ink }}>{lv.approver_role}</span>
                <span style={{ color: T.muted }}>{(lv.approver_names || []).join(', ') || '—'}</span>
                <span className="ml-auto tabular-nums" style={{ color: T.muted }}>
                  {money(lv.threshold_min)}–{lv.threshold_max ? money(lv.threshold_max) : '∞'} · SLA {lv.sla_days}d
                </span>
                <Pill value={lv.action} T={T} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 pt-2">
        {step > 0 && <Btn T={T} variant="ghost" onClick={() => setStep((s) => s - 1)}
          testId="nfa-wizard-back">Back</Btn>}
        {step < STEPS.length - 1 ? (
          <Btn T={T} onClick={next} disabled={busy} testId="nfa-wizard-next">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save &amp; continue
          </Btn>
        ) : (
          <Btn T={T} testId="nfa-wizard-submit" onClick={async () => {
            await api.post(`/nfa/${id}/submit`);
            toast.success('Submitted for approval');
            onCreated?.(id); onClose();
          }}>Submit for approval</Btn>
        )}
        <span className="ml-auto text-[10px]" style={{ color: T.muted }}>
          Autosaves as DRAFT on every step
        </span>
      </div>
    </Drawer>
  );
};

const BulkUpload = ({ onClose, onDone, T }) => {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const validate = async (file) => {
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file); fd.append('mapping', '{}');
    try {
      const r = await api.post('/nfa/upload/validate', fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(r.data);
    } catch (e) { toast.error('Could not read that file'); }
    finally { setBusy(false); }
  };
  return (
    <Drawer open onClose={onClose} T={T} testId="nfa-bulk-drawer" width={760}
      title="Bulk create NFAs from Excel or CSV"
      subtitle={<p className="text-[10.5px] mt-1" style={{ color: T.muted }}>
        Row-level validation, a preview of what will be created, then a partial commit
      </p>}>
      <div className="flex items-center gap-2">
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/nfa/upload/template`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold"
          style={{ background: 'transparent', color: T.body, border: `1px solid ${T.border}` }}
          data-testid="nfa-bulk-template">
          <Download className="w-3.5 h-3.5" /> Download template
        </a>
        <input ref={ref} type="file" accept=".csv,.xlsx" className="hidden" data-testid="nfa-bulk-file"
          onChange={(e) => validate(e.target.files?.[0])} />
        <Btn T={T} onClick={() => ref.current?.click()} disabled={busy} testId="nfa-bulk-choose">
          {busy ? 'Validating…' : 'Choose file'}
        </Btn>
      </div>
      {result && (
        <>
          <div className="flex items-center gap-2 text-[11px]" data-testid="nfa-bulk-summary">
            <Pill value={`${result.summary.valid} valid`} T={T} />
            {result.summary.invalid > 0 && <Pill value={`${result.summary.invalid} invalid`} T={T} />}
            <span style={{ color: T.muted }}>columns: {result.columnsFound.join(', ')}</span>
          </div>
          <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${T.border}` }}>
            {result.rows.map((r) => (
              <div key={r.row} className="px-3 py-1.5 text-[10.5px] flex items-start gap-2"
                style={{ borderTop: `1px solid ${T.border}` }} data-testid={`nfa-bulk-row-${r.row}`}>
                <span className="tabular-nums w-8 shrink-0" style={{ color: T.muted }}>#{r.row}</span>
                <span className="flex-1 truncate" style={{ color: T.ink }}>
                  {r.data.title} · {r.data.project_id} · {money(r.data.requested_amount)}
                </span>                {r.valid ? <Pill value="VALID" T={T} />
                  : <span className="shrink-0" style={{ color: T.red }}>{r.errors.join('; ')}</span>}
              </div>
            ))}
          </div>
          <Btn T={T} testId="nfa-bulk-commit" onClick={async () => {
            const r = await api.post('/nfa/upload/commit', { rows: result.rows.filter((x) => x.valid) });
            toast.success(`${r.data.summary.created} created, ${r.data.summary.failed} failed`);
            onDone?.(); onClose();
          }}>Commit {result.summary.valid} valid rows</Btn>
        </>
      )}
    </Drawer>
  );
};

export const NfaRegister = ({ meta }) => {
  const T = useNfaTheme();
  const activeProject = meta?.activeProject || null;
  const activeProjectId = activeProject?.project_id || '';
  const activeProjectName = activeProject?.name || activeProjectId;
  const [rows, setRows] = useState([]);
  const [facets, setFacets] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useState({ q: '', status: '', nfa_type: '', sla_breach: false });
  const [open, setOpen] = useState(null);
  const [wizard, setWizard] = useState(false);
  const [bulk, setBulk] = useState(false);
  const [saved, setSaved] = useState([]);

  const load = useCallback(() => {
    if (!activeProjectId) { setRows([]); setTotal(0); setLoading(false); return; }
    setLoading(true);
    const qs = new URLSearchParams({ project_id: activeProjectId });
    Object.entries(params).forEach(([k, v]) => { if (v) qs.set(k, v); });
    api.get(`/nfa?${qs.toString()}&limit=100`)
      .then((r) => { setRows(r.data.nfas); setFacets(r.data.facets); setTotal(r.data.total); })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [params, activeProjectId]);
  useEffect(() => { const t = setTimeout(load, 280); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get('/nfa/saved/searches').then((r) => setSaved(r.data.searches)).catch(() => {}); }, []);

  const set = (k) => (e) => setParams((p) => ({ ...p, [k]: e.target.value }));
  const openByNo = (nfaNo) => {
    const hit = rows.find((r) => r.nfa_no === nfaNo);
    if (hit) setOpen(hit.nfa_id);
  };

  return (
    <div className="space-y-4" data-testid="nfa-register">

      <Panel T={T} testId="nfa-filters" pad={false}
        right={<>
          <Btn T={T} variant="ghost" onClick={() => setBulk(true)} testId="nfa-bulk-btn">
            <FileUp className="w-3.5 h-3.5" /> Bulk upload
          </Btn>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/nfa/data/export?q=${encodeURIComponent(params.q)}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold"
            style={{ color: T.body, border: `1px solid ${T.border}` }} data-testid="nfa-export-btn">
            <Download className="w-3.5 h-3.5" /> Export
          </a>
          <Btn T={T} onClick={() => setWizard(true)} testId="nfa-new-btn">
            <Plus className="w-3.5 h-3.5" /> Raise NFA
          </Btn>
        </>}
        title={`NFA Register — ${total} notes on ${activeProjectName || 'no project selected'}`}
        subtitle="Search covers the number, title, justification, vendor and the text inside attachments">
        <div className="p-3 grid grid-cols-1 md:grid-cols-5 gap-2">
          <div className="relative md:col-span-2">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5"
              style={{ color: T.muted }} />
            <Input T={T} value={params.q} onChange={set('q')} data-testid="nfa-search"
              placeholder="Search NFAs and attachment text…" style={{ paddingLeft: 30 }} />
          </div>
          <Select T={T} value={params.status} onChange={set('status')} data-testid="nfa-filter-status">
            <option value="">All statuses</option>
            {(facets.status || []).map((f) => (
              <option key={f.value} value={f.value}>{f.value} ({f.count})</option>))}
          </Select>
          <Select T={T} value={params.nfa_type} onChange={set('nfa_type')} data-testid="nfa-filter-type">
            <option value="">All types</option>
            {(facets.nfa_type || []).map((f) => (
              <option key={f.value} value={f.value}>{f.value} ({f.count})</option>))}
          </Select>
          <div className="text-[10.5px] flex items-center px-2.5 rounded-md"
            data-testid="nfa-filter-project"
            style={{ background: T.isDark ? T.page : '#F4F6FA', border: `1px dashed ${T.border}`,
                     color: T.body }}>
            <span className="truncate">{activeProjectName || '— no project —'}</span>
            <span className="ml-auto text-[9px] uppercase tracking-wider shrink-0"
              style={{ color: T.muted }}>locked</span>
          </div>
        </div>
        <div className="px-3 pb-3 flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-1.5 text-[10.5px]" style={{ color: T.body }}>
            <input type="checkbox" checked={params.sla_breach} data-testid="nfa-filter-sla"
              onChange={(e) => setParams((p) => ({ ...p, sla_breach: e.target.checked }))} />
            SLA breached only
          </label>
          <Btn T={T} variant="ghost" testId="nfa-save-search" onClick={async () => {
            const name = window.prompt('Name this search');
            if (!name) return;
            const r = await api.post('/nfa/saved/searches', { name, params });
            setSaved((s) => [r.data, ...s]); toast.success('Search saved');
          }}>Save this search</Btn>
          {saved.map((s) => (
            <button key={s.searchId} type="button" onClick={() => setParams({ ...params, ...s.params })}
              className="text-[10px] px-2 py-0.5 rounded-full" data-testid={`nfa-saved-${s.searchId}`}
              style={{ background: T.tint(T.primary), color: T.primary }}>{s.name}</button>
          ))}
        </div>
      </Panel>

      <div className="rounded-lg overflow-hidden" style={{ background: T.surface, border: `1px solid ${T.border}` }}>
        <div className="grid grid-cols-12 px-4 py-2 text-[9.5px] uppercase tracking-[0.08em] sticky top-0 z-10"
          style={{ background: T.surfaceAlt, color: T.muted, borderBottom: `1px solid ${T.border}` }}>
          <div className="col-span-2">NFA no</div><div className="col-span-3">Title</div>
          <div className="col-span-2">Project · WBS</div><div className="col-span-1 text-right">Amount</div>
          <div className="col-span-1 text-center">Level</div><div className="col-span-2">Status</div>
          <div className="col-span-1 text-right">Raised</div>
        </div>
        {loading ? (
          <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto"
            style={{ color: T.primary }} /></div>
        ) : rows.length === 0 ? (
          <Empty T={T} testId="nfa-register-empty">No NFAs match these filters.</Empty>
        ) : rows.map((r) => (
          <button key={r.nfa_id} type="button" onClick={() => setOpen(r.nfa_id)}
            className="w-full grid grid-cols-12 px-4 py-2.5 text-left text-[11.5px] items-center hover:opacity-90"
            style={{ borderBottom: `1px solid ${T.border}` }} data-testid={`nfa-row-${r.nfa_no}`}>
            <div className="col-span-2 font-mono text-[10.5px] tabular-nums" style={{ color: T.primary }}>{r.nfa_no}</div>
            <div className="col-span-3 truncate pr-2" style={{ color: T.ink }}>{r.title}</div>
            <div className="col-span-2 truncate pr-2" style={{ color: T.body }}>
              {activeProjectName} · {r.wbs_element}
            </div>
            <div className="col-span-1 text-right tabular-nums font-semibold" style={{ color: T.ink }}>
              {money(r.requested_amount, r.currency)}
            </div>
            <div className="col-span-1 text-center tabular-nums" style={{ color: T.body }}>
              {r.status === 'UNDER_REVIEW' ? `L${r.current_level}` : '—'}
            </div>
            <div className="col-span-2"><Pill value={r.status} T={T} /></div>
            <div className="col-span-1 text-right tabular-nums" style={{ color: T.muted }}>{day(r.raised_on)}</div>
          </button>
        ))}
      </div>

      {open && <NfaDetailDrawer nfaId={open} onClose={() => setOpen(null)} onChanged={load} />}
      {wizard && <CreateWizard meta={meta} T={T} onClose={() => setWizard(false)}
        onCreated={(id) => { load(); setOpen(id); }} />}
      {bulk && <BulkUpload T={T} onClose={() => setBulk(false)} onDone={load} />}
    </div>
  );
};

export default NfaRegister;
